import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Field, Input, Select, TextArea } from '../components/ui/Input';
import { customerChoices, engagementChoices, useFreshRows } from '../lib/select-choices';
import { ExcelButton, FilterTabs, PageHead, StatusPill } from '../components/ui/chrome';
import { useAuth } from '../contexts/AuthContext';
import { apiDelete, apiGet, apiPost, apiPostLive } from '../lib/api-client';
import { useNotice } from '../components/ui/Notice';
import { penceToPoundsInput, poundsToPence } from '../lib/money';
import { enumLabel } from '../lib/labels';

interface Mail {
  id: string;
  kind: string;
  subject: string;
  from: string;
  to?: string[];
  cc?: string[];
  receivedAt?: string;
  body?: string;
  attachments?: string[];
  mailbox?: string;
  loadError?: string;
  linkedId?: string;
  linkedKind?: 'purchase_order' | 'bill' | 'expense' | 'quotation' | 'proposal';
  suggestion?: { action: 'po' | 'bill' | 'expense' | 'accept' | 'offer'; status: 'pending' | 'ready' | 'failed'; draft?: Record<string, unknown>; error?: string };
}

interface Named { id: string; name: string; customerId?: string; customerName?: string; customerCode?: string; code?: string; description?: string }
interface Offer {
  id: string;
  number?: string;
  title?: string;
  status?: string;
  kind?: string;
}

const KINDS: { id: string; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'proposal', label: 'Suggested records' },
  { id: 'po', label: 'Customer orders' },
  { id: 'supplier_bill', label: 'Bills' },
  { id: 'expense_receipt', label: 'Expenses' },
  { id: 'offer_acceptance', label: 'Offer replies' },
  { id: 'offer_request', label: 'Offer requests' },
  { id: 'not_relevant', label: 'Not relevant' },
  { id: 'other', label: 'Other' },
];

function when(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function preview(mail: Mail) {
  const text = (mail.body || mail.loadError || '').replace(/\s+/g, ' ').trim();
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
}

function filedTo(mail: Mail) {
  if (!mail.linkedId) return '';
  if (mail.linkedKind === 'purchase_order') return `/purchase-orders/${mail.linkedId}`;
  if (mail.linkedKind === 'bill' || mail.linkedKind === 'expense') return `/bills/${mail.linkedId}`;
  if (mail.linkedKind === 'quotation') return `/quotations/${mail.linkedId}`;
  if (mail.linkedKind === 'proposal') return `/proposals/${mail.linkedId}`;
  return '';
}

function holdsSuggestion(mail: Mail, action: 'po' | 'bill' | 'expense' | 'accept' | 'offer') {
  return !mail.linkedId && mail.suggestion?.action === action && (mail.suggestion.status === 'ready' || mail.suggestion.status === 'pending');
}

function listTag(mail: Mail): 'Filed' | 'Suggested record' | 'Not relevant' {
  if (mail.kind === 'not_relevant') return 'Not relevant';
  if (mail.linkedId) return 'Filed';
  return 'Suggested record';
}

function needsProposal(mail: Mail) {
  if (mail.kind === 'not_relevant') return false;
  return !mail.linkedId && mail.suggestion?.status !== 'ready' && mail.suggestion?.status !== 'pending';
}

function canFile(can: (page: string, action: string) => boolean, action: 'po' | 'bill' | 'expense' | 'accept' | 'offer') {
  if (action === 'po') return can('purchase_orders', 'ingest');
  if (action === 'bill') return can('bills', 'ingest');
  if (action === 'expense') return can('expenses', 'submit');
  if (action === 'offer') return can('quotations', 'draft');
  return can('quotations', 'issue');
}

function poNumberFrom(mail: Mail) {
  const match = `${mail.subject} ${mail.body || ''}`.match(/\b(?:PO|purchase order)[\s:#-]*([A-Z0-9][A-Z0-9-]{2,})/i);
  return match?.[1] || '';
}

interface DraftLine { description: string; amount: string }
interface ChargeDraft { name: string; kind: string; chargeModel: string; recurrence: string; qty: string; unit: string; billToName: string; billToAddress: string; billToEmail: string }
interface ChatTurn { role: 'user' | 'assistant'; text: string }

const CHARGE_KINDS = ['hosting', 'support', 'tnm', 'milestone', 'licence', 'services', 'other'] as const;
const CHARGE_MODELS = ['recurring', 'project', 'product'] as const;
const CHARGE_RECURRENCE = ['none', 'monthly', 'quarterly', 'annual'] as const;

function draftText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function draftDate(value: unknown, fallback: string): string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : fallback;
}

function draftMoney(value: unknown): string {
  const pence = Number(value);
  return Number.isInteger(pence) && pence > 0 ? penceToPoundsInput(pence) : '';
}

function draftLines(value: unknown): DraftLine[] {
  if (!Array.isArray(value)) return [{ description: '', amount: '' }];
  const lines = value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as { description?: unknown; amountPence?: unknown };
    return [{ description: draftText(row.description), amount: draftMoney(row.amountPence) }];
  });
  return lines.length ? lines : [{ description: '', amount: '' }];
}

function coverOf(draft: Record<string, unknown>): 'proposal' | 'quotation' | 'both' {
  if (draft.cover === 'proposal' || draft.cover === 'quotation' || draft.cover === 'both') return draft.cover;
  const title = draftText(draft.title).trim();
  const narrative = [draft.purpose, draft.scope, draft.summary].some((value) => {
    const text = draftText(value).trim();
    return text.length >= 20 && text !== title;
  });
  const priced = Array.isArray(draft.lines) && draft.lines.some((line) => {
    if (!line || typeof line !== 'object') return false;
    const amount = Number((line as { unitPence?: unknown }).unitPence);
    return Number.isFinite(amount) && amount > 0;
  });
  if (title && narrative && priced) return 'both';
  if (priced) return 'quotation';
  if (title && narrative) return 'proposal';
  return 'both';
}

function draftQuoteLines(value: unknown): { description: string; qty: string; unit: string }[] {
  if (!Array.isArray(value)) return [{ description: '', qty: '1', unit: '' }];
  const lines = value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as { description?: unknown; qty?: unknown; unitPence?: unknown };
    const description = draftText(row.description);
    if (!description) return [];
    const qty = Number(row.qty);
    return [{ description, qty: String(Number.isFinite(qty) && qty > 0 ? qty : 1), unit: draftMoney(row.unitPence) }];
  });
  return lines.length ? lines : [{ description: '', qty: '1', unit: '' }];
}

function oneOf(value: unknown, allowed: readonly string[], fallback: string) {
  const text = typeof value === 'string' ? value : '';
  return allowed.includes(text) ? text : fallback;
}

function draftCharges(value: unknown): ChargeDraft[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as { name?: unknown; kind?: unknown; chargeModel?: unknown; recurrence?: unknown; qty?: unknown; unitPence?: unknown; billToName?: unknown; billToAddress?: unknown; billToEmail?: unknown };
    const name = draftText(row.name);
    if (!name) return [];
    const qty = Number(row.qty);
    return [{
      name,
      kind: oneOf(row.kind, CHARGE_KINDS, 'other'),
      chargeModel: oneOf(row.chargeModel, CHARGE_MODELS, 'project'),
      recurrence: oneOf(row.recurrence, CHARGE_RECURRENCE, 'none'),
      qty: Number.isFinite(qty) && qty > 0 ? String(qty) : '1',
      unit: draftMoney(row.unitPence),
      billToName: draftText(row.billToName),
      billToAddress: draftText(row.billToAddress),
      billToEmail: draftText(row.billToEmail),
    }];
  });
}

const DRAFT_LABEL = {
  po: 'Drafting the purchase order from the email',
  bill: 'Drafting the bill from the email',
  expense: 'Drafting the expense from the email',
  accept: 'Reading the email and matching the offer',
  offer: 'Drafting the offer from the email',
} as const;

const SAVE_LABEL = {
  po: 'Saving the purchase order and copying the email to SharePoint',
  bill: 'Saving the bill and copying the email to SharePoint',
  expense: 'Saving the expense and copying the email to SharePoint',
  accept: 'Accepting the offer and copying the email to SharePoint',
  offer: 'Saving the offer and copying the email to SharePoint',
} as const;

const SAVED = {
  po: 'Customer order saved',
  bill: 'Bill saved',
  expense: 'Expense saved',
  accept: 'Offer accepted',
  offer: 'Offer saved',
} as const;

interface InboxPageResult {
  items: Mail[];
  nextCursor: string;
  counts: Record<string, number>;
}

export function InboxPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const notice = useNotice();
  const qc = useQueryClient();
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [action, setAction] = useState<'po' | 'bill' | 'expense' | 'accept' | 'offer' | ''>('');
  const [askDelete, setAskDelete] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(q.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [q]);
  const query = useInfiniteQuery({
    queryKey: ['inbox', tab, search, from, to],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ kind: tab, q: search, from, to });
      if (pageParam) params.set('cursor', pageParam);
      return apiGet<InboxPageResult>(`/api/inbox?${params}`);
    },
    getNextPageParam: (last) => last.nextCursor || undefined,
    refetchInterval: (current) => (current.state.data?.pages || []).some((page) => page.items.some((row) => row.suggestion?.status === 'pending')) ? 4000 : false,
  });
  const [engagements, reloadEngagements] = useFreshRows<Named>('/api/engagements', action === 'po' || action === 'offer');
  const [customers, reloadCustomers] = useFreshRows<Named>('/api/customers', action === 'po' || action === 'offer');
  const vendors = useQuery({ queryKey: ['vendors'], queryFn: () => apiGet<Named[]>('/api/vendors'), enabled: action === 'bill' });
  const offers = useQuery({ queryKey: ['commercial'], queryFn: () => apiGet<Offer[]>('/api/commercial'), enabled: action === 'accept' });
  const rows = query.data?.pages.flatMap((page) => page.items) || [];
  const selected = rows.find((row) => row.id === selectedId) || rows[0];
  const counts = query.data?.pages[0]?.counts || {};
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage, rows.length]);

  const remove = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('Select a message');
      return apiDelete(`/api/inbox/${selected.id}`);
    },
    onSuccess: async () => {
      setAskDelete(false);
      setSelectedId('');
      await qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });

  const file = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (!selected) throw new Error('Select a message');
      if (action === 'po') return apiPost(`/api/inbox/${selected.id}/file-po`, body);
      if (action === 'bill') return apiPost(`/api/inbox/${selected.id}/file-bill`, body);
      if (action === 'expense') return apiPost(`/api/inbox/${selected.id}/file-expense`, body);
      if (action === 'offer') return apiPost(`/api/inbox/${selected.id}/file-offer`, body);
      return apiPost(`/api/inbox/${selected.id}/accept`, body);
    },
    onSuccess: async () => {
      setAction('');
      await qc.invalidateQueries({ queryKey: ['inbox'] });
      await qc.invalidateQueries({ queryKey: ['commercial'] });
    },
  });

  return (
    <div className="flex h-[calc(100vh-6.5rem)] min-h-0 flex-col overflow-hidden">
      <PageHead crumbs={[{ label: 'Inbox' }]} title="Inbox" subtitle="Read the message, then file it or mark the offer accepted." actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => void (async () => {
            const params = new URLSearchParams({ kind: tab, q: search, from, to, targets: 'proposal' });
            const page = await apiGet<InboxPageResult>(`/api/inbox?${params}`);
            const targets = page.items;
            if (!targets.length) {
              notice.success('No messages need a proposal');
              return;
            }
            const results = await notice.runGroup('Draft proposals', targets.map((row) => ({
              title: row.subject || 'Draft proposal',
              work: (log, signal) => apiPostLive<{ status: string }>(`/api/inbox/${row.id}/suggest`, {}, log, signal),
            })), { confirm: `This will draft suggestions for ${targets.length} messages that are not filed and do not already have a proposal. Each one runs at the same time. Nothing is saved as a record.` });
            if (!results) return;
            await qc.invalidateQueries({ queryKey: ['inbox'] });
            if (targets.length <= 1) {
              const row = results[0];
              if (row?.status === 'ready') notice.success('Proposal drafted');
              else if (row) notice.success('No record could be drafted from this message');
              return;
            }
            const drafted = results.filter((row) => row?.status === 'ready').length;
            notice.success(`Drafted ${drafted}. Skipped ${results.length - drafted}.`);
          })()}>Draft Proposals</Button>
          <Button variant="outline" onClick={() => void query.refetch()}>Refresh</Button>
        </div>
      } />
      <div className={selectedId ? 'max-md:hidden' : ''}>
      <FilterTabs tabs={KINDS.map((item) => ({ ...item, count: counts[item.id] || 0 }))} active={tab} onChange={setTab} />
      <div className="bo-filter-row mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Input aria-label="Search mail" placeholder="Search subject, sender, or message" value={q} onChange={(event) => setQ(event.target.value)} />
        </div>
        <Field label="From"><Input aria-label="From date" type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></Field>
        <Field label="To"><Input aria-label="To date" type="date" value={to} onChange={(event) => setTo(event.target.value)} /></Field>
        <ExcelButton filename="Inbox" load={async () => {
          const items: Mail[] = [];
          let cursor = '';
          for (;;) {
            const params = new URLSearchParams({ kind: tab, q: search, from, to });
            if (cursor) params.set('cursor', cursor);
            const page = await apiGet<InboxPageResult>(`/api/inbox?${params}`);
            items.push(...page.items);
            if (!page.nextCursor || page.nextCursor === cursor) break;
            cursor = page.nextCursor;
          }
          return {
            headers: ['Received', 'From', 'Subject', 'Kind'],
            rows: items.map((mail) => [mail.receivedAt || '', mail.from || '', mail.subject || '', mail.kind || '']),
          };
        }} />
      </div>
      </div>
      <div className="grid min-h-0 flex-1 gap-4 max-md:block lg:grid-cols-[340px_minmax(0,1fr)]">
        <div className={`min-h-0 overflow-y-auto rounded-xl border border-[var(--bo-border)] bg-[var(--bo-canvas)] ${selectedId ? 'max-md:hidden' : ''}`}>
            {query.isLoading ? <p className="p-4 text-sm text-[var(--bo-muted)]">Loading mail</p> : null}
            {!query.isLoading && !rows.length ? <p className="p-4 text-sm text-[var(--bo-muted)]">No messages in this view.</p> : null}
            {rows.map((row) => {
              const on = row.id === selected?.id;
              return (
                <button key={row.id} type="button" className={`block w-full border-b border-[var(--bo-border)] px-4 py-3 text-left ${on ? 'bg-[var(--bo-pill)]' : 'hover:bg-[var(--bo-pill)]'}`} onClick={() => setSelectedId(row.id)}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate text-sm font-semibold">{row.from || 'Unknown sender'}</span>
                    <span className="shrink-0 text-xs text-[var(--bo-faint)]">{when(row.receivedAt)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="truncate text-sm">{row.subject}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${listTag(row) === 'Filed' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200' : listTag(row) === 'Suggested record' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200' : 'bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-200'}`}>{listTag(row)}</span>
                  </div>
                  <div className="mt-1 truncate text-xs text-[var(--bo-muted)]">{preview(row) || 'No message body yet'}</div>
                </button>
              );
            })}
            <div ref={sentinel} className="h-8" />
            {query.isFetchingNextPage ? <p className="px-4 pb-4 text-sm text-[var(--bo-muted)]">Loading more</p> : null}
        </div>
        <div className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-[var(--bo-border)] bg-[var(--bo-canvas)] ${selectedId ? '' : 'max-md:hidden'}`}>
          {!selected ? <p className="p-6 text-sm text-[var(--bo-muted)]">Select a message to read it.</p> : (
            <>
              <div className="shrink-0 border-b border-[var(--bo-border)] px-6 py-5">
                <button type="button" className="mb-3 text-sm text-[var(--bo-link)] md:hidden" onClick={() => setSelectedId('')}>Return to list</button>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="text-xl font-semibold leading-snug">{selected.subject}</h2>
                  <StatusPill value={selected.kind === 'po' ? 'customer order' : selected.kind} />
                </div>
                <dl className="mt-4 space-y-1 text-sm">
                  <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2"><dt className="text-[var(--bo-muted)]">From</dt><dd>{selected.from || 'Unknown'}</dd></div>
                  {selected.mailbox ? <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2"><dt className="text-[var(--bo-muted)]">Mailbox</dt><dd>{selected.mailbox}</dd></div> : null}
                  <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2"><dt className="text-[var(--bo-muted)]">To</dt><dd>{selected.to?.join(', ') || 'Not loaded'}</dd></div>
                  {selected.cc?.length ? <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2"><dt className="text-[var(--bo-muted)]">Cc</dt><dd>{selected.cc.join(', ')}</dd></div> : null}
                  <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2"><dt className="text-[var(--bo-muted)]">Received</dt><dd>{when(selected.receivedAt) || 'Not loaded'}</dd></div>
                  {selected.attachments?.length ? <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2"><dt className="text-[var(--bo-muted)]">Attachments</dt><dd className="flex flex-wrap gap-x-3 gap-y-1">{selected.attachments.map((name, index) => <a key={`${index}-${name}`} className="text-[var(--bo-link)]" href={`/api/inbox/${selected.id}/attachments/${index}`} target="_blank" rel="noreferrer">{name}</a>)}</dd></div> : null}
                </dl>
                <div className="mt-4 flex flex-wrap gap-2">
                  {filedTo(selected) ? <Link className="mr-2 self-center text-sm text-[var(--bo-link)]" to={filedTo(selected)}>Open filed record</Link> : null}
                  {selected.suggestion?.status === 'pending' ? <p className="self-center text-sm text-[var(--bo-muted)]">{selected.kind === 'offer_acceptance' ? 'Matching the issued offer.' : selected.kind === 'offer_request' ? 'Drafting the offer.' : 'Drafting a suggested record.'}</p> : null}
                  {selected.suggestion?.status === 'failed' ? <p className="self-center text-sm text-[var(--bo-muted)]">{selected.suggestion.error || 'The suggestion did not finish.'}</p> : null}
                  {selected.suggestion?.status === 'ready' && !selected.linkedId && canFile(can, selected.suggestion.action) ? <Button onClick={() => setAction(selected.suggestion!.action)}>{selected.suggestion.action === 'accept' ? 'Show suggested update' : selected.suggestion.action === 'offer' ? 'Show suggested offer' : 'Show suggested record'}</Button> : null}
                  {needsProposal(selected) && can('inbox', 'read') ? <Button onClick={() => void notice.run('Draft proposal', async (log, signal) => {
                    const result = await apiPostLive<{ status: string }>(`/api/inbox/${selected.id}/suggest`, {}, log, signal);
                    await qc.invalidateQueries({ queryKey: ['inbox'] });
                    return result;
                  }, (result) => result.status === 'ready' ? 'Proposal drafted' : 'No record could be drafted from this message', { confirm: `This will classify "${selected.subject}" and draft an unsaved suggestion. It does not file a record.` })}>Draft proposal</Button> : null}
                  {can('purchase_orders', 'ingest') && !holdsSuggestion(selected, 'po') ? <Button variant="outline" onClick={() => void notice.confirm('File as customer order', 'This will draft a customer order from this email. It does not save until you create the record.').then((ok) => { if (ok) setAction('po'); })}>File as customer order</Button> : null}
                  {can('bills', 'ingest') && !holdsSuggestion(selected, 'bill') ? <Button variant="outline" onClick={() => void notice.confirm('File as bill', 'This will draft a bill from this email. It does not save until you create the record.').then((ok) => { if (ok) setAction('bill'); })}>File as bill</Button> : null}
                  {can('expenses', 'submit') && !holdsSuggestion(selected, 'expense') ? <Button variant="outline" onClick={() => void notice.confirm('File as expense', 'This will draft an expense from this email. It does not save until you create the record.').then((ok) => { if (ok) setAction('expense'); })}>File as expense</Button> : null}
                  {can('quotations', 'draft') && !holdsSuggestion(selected, 'offer') ? <Button variant="outline" onClick={() => void notice.confirm('File as offer', 'This will draft a proposal, a quotation, or both from this email. It does not save until you create the offer.').then((ok) => { if (ok) setAction('offer'); })}>File as offer</Button> : null}
                  {can('quotations', 'issue') && !holdsSuggestion(selected, 'accept') ? <Button variant="outline" onClick={() => void notice.confirm('Accept offer', 'This will propose marking the issued quotation or proposal accepted. It does not create a new record.').then((ok) => { if (ok) setAction('accept'); })}>Accept offer</Button> : null}
                  {can('inbox', 'ingest') && selected.kind !== 'not_relevant' ? <Button variant="outline" onClick={() => void notice.confirm('Not relevant', 'This marks the message not relevant. A suggestion is dropped. A record already filed from it stays.').then(async (ok) => { if (!ok) return; await apiPost(`/api/inbox/${selected.id}/irrelevant`, {}); await qc.invalidateQueries({ queryKey: ['inbox'] }); })}>Not relevant</Button> : null}
                  {can('inbox', 'ingest') ? <Button variant="outline" onClick={() => setAskDelete(true)}>Delete</Button> : null}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-5">
                {selected.loadError ? <p className="text-sm text-[var(--bo-muted)]">{selected.loadError}</p> : null}
                <pre className="whitespace-pre-wrap font-sans text-sm leading-6">{selected.body || (selected.loadError ? '' : 'The message body has not loaded yet. Refresh to try the mailbox again.')}</pre>
              </div>
            </>
          )}
        </div>
      </div>
      {action && selected ? (
        <FileDialog
          action={action}
          mail={selected}
          suggested={selected.suggestion?.status === 'ready' && selected.suggestion.action === action ? selected.suggestion.draft : undefined}
          engagements={engagements}
          customers={customers}
          reloadEngagements={reloadEngagements}
          reloadCustomers={reloadCustomers}
          canCreate={can('engagements', 'edit')}
          vendors={vendors.data || []}
          offers={(offers.data || []).filter((row) => row.status === 'issued')}
          onClose={() => setAction('')}
          onSubmit={async (body) => {
            const row = await notice.run(SAVE_LABEL[action], () => file.mutateAsync(body), SAVED[action], { confirm: false }) as { id?: string; proposalId?: string; quotationId?: string } | undefined;
            if (!row) return;
            if (action === 'po') navigate(`/purchase-orders/${row.id}`);
            else if (action === 'bill' || action === 'expense') navigate(`/bills/${row.id}`);
            else if (action === 'offer') navigate(row.proposalId ? `/proposals/${row.proposalId}` : `/quotations/${row.quotationId || row.id}`);
            else navigate(body.proposalId ? `/proposals/${row.id}` : `/quotations/${row.id}`);
          }}
        />
      ) : null}
      {askDelete && selected ? (
        <Dialog title="Delete message" onClose={() => setAskDelete(false)} footer={
          <>
            <Button variant="outline" onClick={() => setAskDelete(false)}>Cancel</Button>
            <Button variant="destructive" disabled={remove.isPending} onClick={() => void notice.run('Deleting the message', () => remove.mutateAsync(), 'Message deleted', { confirm: false })}>Delete</Button>
          </>
        }>
          <p className="text-sm">Delete this message from the inbox. A purchase order, bill, expense, or offer already filed from it stays.</p>
        </Dialog>
      ) : null}
    </div>
  );
}

function FileDialog({ action, mail, suggested, engagements, customers, reloadEngagements, reloadCustomers, canCreate, vendors, offers, onClose, onSubmit }: {
  action: 'po' | 'bill' | 'expense' | 'accept' | 'offer';
  mail: Mail;
  suggested?: Record<string, unknown>;
  engagements: Named[];
  customers: Named[];
  reloadEngagements: () => Promise<void>;
  reloadCustomers: () => Promise<void>;
  canCreate: boolean;
  vendors: Named[];
  offers: Offer[];
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<unknown>;
}) {
  const notice = useNotice();
  const qc = useQueryClient();
  const today = (mail.receivedAt || new Date().toISOString()).slice(0, 10);
  const [engagementId, setEngagementId] = useState('');
  const [poNumber, setPoNumber] = useState(poNumberFrom(mail));
  const [lines, setLines] = useState<DraftLine[]>([{ description: '', amount: '' }]);
  const [charges, setCharges] = useState<ChargeDraft[]>([]);
  const [chat, setChat] = useState<ChatTurn[]>([]);
  const [followUp, setFollowUp] = useState('');
  const [revising, setRevising] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [number, setNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [expiresOn, setExpiresOn] = useState('');
  const [billToName, setBillToName] = useState('');
  const [billToAddress, setBillToAddress] = useState('');
  const [billToEmail, setBillToEmail] = useState('');
  const [qty, setQty] = useState('1');
  const [unit, setUnit] = useState<'hour' | 'day'>('day');
  const [periodStart, setPeriodStart] = useState(today);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [dueOn, setDueOn] = useState(today);
  const [claimant, setClaimant] = useState(mail.from.includes('@') ? mail.from : '');
  const [preApproved, setPreApproved] = useState(false);
  const [customerId, setCustomerId] = useState('');
  const [offerTitle, setOfferTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [scope, setScope] = useState('');
  const [summary, setSummary] = useState('');
  const [docKind, setDocKind] = useState('services');
  const [preparedFor, setPreparedFor] = useState('');
  const [cover, setCover] = useState<'proposal' | 'quotation' | 'both'>('both');
  const [offerTab, setOfferTab] = useState<'proposal' | 'quotation'>('proposal');
  const [quoteLines, setQuoteLines] = useState<{ description: string; qty: string; unit: string }[]>([{ description: '', qty: '1', unit: '' }]);
  const mentioned = offers.find((row) => row.number && `${mail.subject} ${mail.body || ''}`.includes(row.number));
  const [offerId, setOfferId] = useState(mentioned ? `${mentioned.kind}:${mentioned.id}` : '');
  const [created, setCreated] = useState<Named[]>([]);
  const [askCreate, setAskCreate] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [engagementDraft, setEngagementDraft] = useState<{ customerId: string; customerName: string; customerCode: string; name: string; status: string; stage: string } | null>(null);
  const title = suggested
    ? (action === 'po' ? 'Suggested customer order' : action === 'bill' ? 'Suggested bill' : action === 'expense' ? 'Suggested expense' : action === 'offer' ? 'Suggested offer' : 'Mark offer accepted')
    : (action === 'po' ? 'File as customer order' : action === 'bill' ? 'File as bill' : action === 'expense' ? 'File as expense' : action === 'offer' ? 'File as offer' : 'Accept offer');
  const engagementOptions = [...engagements, ...created.filter((row) => !engagements.some((item) => item.id === row.id))];

  function applyDraft(draft: Record<string, unknown>) {
    const nextCurrency = draftText(draft.currency);
    if (nextCurrency === 'GBP' || nextCurrency === 'EUR' || nextCurrency === 'USD') setCurrency(nextCurrency);
    if (action === 'po') {
      setEngagementId(draftText(draft.engagementId));
      if (draftText(draft.poNumber)) setPoNumber(draftText(draft.poNumber));
      setExpiresOn(draftDate(draft.expiresOn, ''));
      setBillToName(draftText(draft.billToName));
      setBillToAddress(draftText(draft.billToAddress));
      setBillToEmail(draftText(draft.billToEmail));
      setLines(draftLines(draft.lines));
      setCharges(draftCharges(draft.chargeLines));
      return;
    }
    if (action === 'bill') {
      setVendorId(draftText(draft.vendorId));
      setNumber(draftText(draft.number));
      setAmount(draftMoney(draft.amountPence));
      setQty(String(Number(draft.qty) > 0 ? Number(draft.qty) : 1));
      setUnit(draft.unit === 'hour' ? 'hour' : 'day');
      setPeriodStart(draftDate(draft.periodStart, today));
      setPeriodEnd(draftDate(draft.periodEnd, today));
      setDueOn(draftDate(draft.dueOn, today));
      return;
    }
    if (action === 'expense') {
      if (draftText(draft.claimantEmail)) setClaimant(draftText(draft.claimantEmail));
      setNumber(draftText(draft.number));
      setAmount(draftMoney(draft.amountPence));
      setDueOn(draftDate(draft.dueOn, today));
      setPreApproved(draft.preApproved === true);
      return;
    }
    if (draftText(draft.quotationId)) setOfferId(`quotation:${draftText(draft.quotationId)}`);
    else if (draftText(draft.proposalId)) setOfferId(`proposal:${draftText(draft.proposalId)}`);
    if (action === 'offer') {
      setCustomerId(draftText(draft.customerId));
      setEngagementId(draftText(draft.engagementId));
      setOfferTitle(draftText(draft.title));
      setPurpose(draftText(draft.purpose));
      setScope(draftText(draft.scope));
      setSummary(draftText(draft.summary));
      setDocKind(draftText(draft.docKind) || 'services');
      setPreparedFor(draftText(draft.preparedFor));
      setBillToName(draftText(draft.billToName));
      setBillToAddress(draftText(draft.billToAddress));
      setBillToEmail(draftText(draft.billToEmail) || (mail.from.includes('@') ? mail.from : ''));
      const nextCover = coverOf(draft);
      setCover(nextCover);
      setOfferTab(nextCover === 'quotation' ? 'quotation' : 'proposal');
      setQuoteLines(draftQuoteLines(draft.lines));
    }
  }

  useEffect(() => {
    if (suggested) {
      applyDraft(suggested);
      return;
    }
    let live = true;
    void notice.run(DRAFT_LABEL[action], async (log, signal) => {
      const result = await apiPostLive<{ draft: Record<string, unknown> }>(`/api/inbox/${mail.id}/draft`, { kind: action }, log, signal);
      if (!live || signal.aborted) return;
      applyDraft(result.draft || {});
    }, 'Draft ready', { confirm: false });
    return () => { live = false; };
  }, [action, mail.id]);

  function submit() {
    if (action === 'po') {
      const priced = lines.map((line) => ({ description: line.description.trim(), amountPence: poundsToPence(line.amount) }));
      const chargeRows = charges.map((line) => ({
        name: line.name.trim(),
        kind: line.kind,
        chargeModel: line.chargeModel,
        recurrence: line.recurrence,
        qty: Number(line.qty) || 0,
        unitPence: poundsToPence(line.unit),
        billToName: line.billToName.trim(),
        billToAddress: line.billToAddress.trim(),
        billToEmail: line.billToEmail.trim(),
      }));
      if (!engagementId || !poNumber.trim() || !priced.length || priced.some((line) => !line.description || line.amountPence <= 0) || !chargeRows.length || chargeRows.some((line) => !line.name || line.qty <= 0 || line.unitPence <= 0)) {
        notice.failure('The purchase order needs an engagement, a number, a line amount from the email, and a charge line with a name, a quantity, and a unit price.');
        return;
      }
      void onSubmit({ engagementId, poNumber: poNumber.trim(), valuePence: priced.reduce((sum, line) => sum + line.amountPence, 0), currency, expiresOn, billToName: billToName.trim(), billToAddress: billToAddress.trim(), billToEmail: billToEmail.trim(), lines: priced, chargeLines: chargeRows });
      return;
    }
    const pence = poundsToPence(amount);
    if (action === 'bill') {
      if (!vendorId || !number.trim() || !periodStart || !periodEnd || !dueOn || !amount.trim() || pence <= 0) {
        notice.failure('The bill needs a vendor, a number, dates, and an amount from the email.');
        return;
      }
      void onSubmit({ vendorId, number: number.trim(), amountPence: pence, currency, qty: Number(qty) || 1, unit, periodStart, periodEnd, dueOn });
      return;
    }
    if (action === 'expense') {
      if (!claimant.trim() || !number.trim() || !dueOn || !amount.trim() || pence <= 0) {
        notice.failure('The expense needs a claimant, a number, a due date, and an amount from the email.');
        return;
      }
      void onSubmit({ claimantEmail: claimant.trim(), number: number.trim(), amountPence: pence, currency, dueOn, preApproved });
      return;
    }
    if (action === 'offer') {
      const priced = quoteLines.map((line) => ({ description: line.description.trim(), qty: Number(line.qty) || 1, unitPence: poundsToPence(line.unit) }));
      if (!customerId || !offerTitle.trim()) {
        notice.failure('The offer needs a customer and a title.');
        return;
      }
      if ((cover === 'quotation' || cover === 'both') && priced.every((line) => !line.description || line.unitPence <= 0)) {
        notice.failure('The quotation needs a line with a description and a unit price.');
        return;
      }
      void onSubmit({
        customerId,
        engagementId,
        title: offerTitle.trim(),
        purpose: purpose.trim(),
        scope: scope.trim(),
        summary: summary.trim(),
        docKind,
        currency,
        cover,
        preparedFor: preparedFor.trim(),
        billToName: billToName.trim(),
        billToAddress: billToAddress.trim(),
        billToEmail: billToEmail.trim(),
        lines: priced.filter((line) => line.description && line.unitPence > 0),
      });
      return;
    }
    const [kind, id] = offerId.split(':');
    if (!id) {
      notice.failure('Choose the issued quotation or proposal this email accepts.');
      return;
    }
    void onSubmit(kind === 'proposal' ? { proposalId: id } : { quotationId: id });
  }

  function currentDraft(): Record<string, unknown> {
    if (action === 'po') {
      return {
        engagementId,
        poNumber,
        currency,
        expiresOn,
        billToName,
        billToAddress,
        billToEmail,
        lines: lines.map((line) => ({ description: line.description.trim(), amountPence: poundsToPence(line.amount) })),
        chargeLines: charges.map((line) => ({
          name: line.name.trim(),
          kind: line.kind,
          chargeModel: line.chargeModel,
          recurrence: line.recurrence,
          qty: Number(line.qty) || 1,
          unitPence: poundsToPence(line.unit),
          billToName: line.billToName.trim(),
          billToAddress: line.billToAddress.trim(),
          billToEmail: line.billToEmail.trim(),
        })),
      };
    }
    if (action === 'bill') return { vendorId, number, amountPence: poundsToPence(amount), currency, qty: Number(qty) || 1, unit, periodStart, periodEnd, dueOn };
    if (action === 'expense') return { claimantEmail: claimant, number, amountPence: poundsToPence(amount), currency, dueOn, preApproved };
    if (action === 'offer') {
      return {
        customerId,
        engagementId,
        title: offerTitle,
        purpose,
        scope,
        summary,
        docKind,
        currency,
        preparedFor,
        billToName,
        billToAddress,
        billToEmail,
        cover,
        lines: quoteLines.map((line) => ({ description: line.description.trim(), qty: Number(line.qty) || 1, unitPence: poundsToPence(line.unit) })),
      };
    }
    const [kind, id] = offerId.split(':');
    return kind === 'proposal' ? { proposalId: id || '' } : { quotationId: id || '' };
  }

  async function sendFollowUp() {
    const text = followUp.trim();
    if (!text || revising) return;
    setRevising(true);
    setChat((rows) => [...rows, { role: 'user', text }]);
    setFollowUp('');
    try {
      const result = await notice.run('Updating the draft', (log, signal) => apiPostLive<{ draft: Record<string, unknown> }>(`/api/inbox/${mail.id}/revise-po`, {
        kind: action,
        instruction: text,
        draft: currentDraft(),
      }, log, signal), 'Draft updated');
      if (!result?.draft) return;
      applyDraft(result.draft);
      setChat((rows) => [...rows, { role: 'assistant', text: action === 'accept' ? 'Updated the offer. Check it, then mark it accepted, discard it, or send another instruction.' : 'Updated the draft. Check it, then create the record, discard it, or send another instruction.' }]);
      await qc.invalidateQueries({ queryKey: ['inbox'] });
    } finally {
      setRevising(false);
    }
  }

  async function discardSuggestion() {
    const ok = await notice.run('Discarding the suggestion', () => apiPost(`/api/inbox/${mail.id}/discard-suggestion`), 'Suggestion discarded');
    if (ok === undefined) return;
    await qc.invalidateQueries({ queryKey: ['inbox'] });
    onClose();
  }

  async function proceedCreate() {
    setDrafting(true);
    try {
      const result = await notice.run('Drafting the engagement', (log, signal) => apiPostLive<{ draft: { customerId?: string; customerName?: string; customerCode?: string; name?: string; status?: string; stage?: string } }>(`/api/inbox/${mail.id}/draft-engagement`, { instruction }, log, signal), 'Engagement draft ready', { confirm: false });
      const draft = result?.draft;
      if (!draft) return;
      setEngagementDraft({
        customerId: draftText(draft.customerId),
        customerName: draftText(draft.customerName),
        customerCode: draftText(draft.customerCode),
        name: draftText(draft.name),
        status: draft.status === 'proposed' || draft.status === 'contracted' || draft.status === 'active' || draft.status === 'closed' ? draft.status : 'contracted',
        stage: draft.stage === 'lead' || draft.stage === 'opportunity' || draft.stage === 'proposal' || draft.stage === 'quotation' || draft.stage === 'contracted' || draft.stage === 'closed' ? draft.stage : 'contracted',
      });
      setAskCreate(false);
    } finally {
      setDrafting(false);
    }
  }

  async function saveEngagement() {
    if (!engagementDraft) return;
    const name = engagementDraft.name.trim();
    const customerId = engagementDraft.customerId;
    const customerName = engagementDraft.customerName.trim();
    const customerCode = engagementDraft.customerCode.trim();
    if (!name || (!customerId && (!customerName || !customerCode))) {
      notice.failure('The engagement needs a name, and a customer or a new customer name and code.');
      return;
    }
    const row = await notice.run('Saving the engagement', async () => {
      let nextCustomer = customerId;
      if (!nextCustomer) {
        const parent = await apiPost<Named>('/api/reporting-customers', { name: customerName, code: customerCode });
        const customer = await apiPost<Named>('/api/customers', { name: customerName, legalName: customerName, code: customerCode, reportingCustomerId: parent.id, relationship: 'customer' });
        nextCustomer = customer.id;
      }
      return apiPost<Named>('/api/engagements', { customerId: nextCustomer, name, status: engagementDraft.status, stage: engagementDraft.stage });
    }, 'Engagement saved', { confirm: false });
    if (!row?.id) return;
    const customer = customers.find((item) => item.id === (customerId || row.customerId));
    setCreated((rows) => [...rows, { ...row, customerName: row.customerName || customer?.name || customerName }]);
    setEngagementId(row.id);
    setEngagementDraft(null);
    await reloadEngagements();
    await reloadCustomers();
  }

  return (
    <>
    <Dialog title={title} large onClose={onClose} footer={
      <>
        {suggested ? <Button variant="outline" onClick={() => void discardSuggestion()}>Discard suggestion</Button> : null}
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit}>{suggested ? (action === 'accept' ? 'Mark accepted' : action === 'offer' ? (cover === 'both' ? 'Create both' : cover === 'quotation' ? 'Create quotation' : 'Create proposal') : 'Create record') : action === 'offer' ? (cover === 'both' ? 'Create both' : cover === 'quotation' ? 'Create quotation' : 'Create proposal') : 'Save'}</Button>
      </>
    }>
      <p className="text-sm text-[var(--bo-muted)]">{suggested ? (action === 'accept' ? 'This proposes marking the issued offer accepted. It does not create a new record. Check the offer, then mark it accepted or discard the suggestion.' : action === 'offer' ? 'This drafts a proposal, a quotation, or both. Nothing is saved until you create it. Discard the suggestion to leave the inbox as it is.' : 'This suggestion is not saved yet. Change it here, tell the assistant what to amend, then create the record or discard the suggestion.') : action === 'offer' ? 'The agent drafted what the email supports. Choose a proposal, a quotation, or both, then create it. Cancel leaves the inbox as it is.' : 'The agent drafted this from the email. Change anything that is wrong, then save. Cancel leaves the inbox as it is.'}</p>
      <p className="text-sm font-medium">{mail.subject}</p>
      {action === 'po' ? (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Engagement">
              <div className="flex gap-2">
                <Select className="min-w-0 flex-1" searchable choices={engagementChoices(engagementOptions)} onOpen={reloadEngagements} value={engagementId} onChange={(event) => setEngagementId(event.target.value)} />
                {canCreate ? <Button variant="outline" onClick={() => setAskCreate(true)}>Create</Button> : null}
              </div>
            </Field>
            <Field label="PO number"><Input value={poNumber} onChange={(event) => setPoNumber(event.target.value)} /></Field>
            <Field label="Currency"><Select value={currency} onChange={(event) => setCurrency(event.target.value)}><option>GBP</option><option>EUR</option><option>USD</option></Select></Field>
            <Field label="Expires"><Input type="date" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} /><span className="mt-1 block text-xs text-[var(--bo-muted)]">Leave blank if the order does not expire.</span></Field>
            <Field label="Bill to"><Input value={billToName} onChange={(event) => setBillToName(event.target.value)} /></Field>
            <Field label="Address"><TextArea value={billToAddress} onChange={(event) => setBillToAddress(event.target.value)} /></Field>
            <Field label="Email"><Input value={billToEmail} onChange={(event) => setBillToEmail(event.target.value)} /></Field>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-[minmax(0,1fr)_160px_40px] gap-2 text-xs text-[var(--bo-muted)]"><span>Description</span><span>Amount</span><span /></div>
            {lines.map((line, index) => (
              <div key={index} className="grid grid-cols-[minmax(0,1fr)_160px_40px] gap-2">
                <Input aria-label={`Line ${index + 1} description`} value={line.description} onChange={(event) => setLines(lines.map((item, at) => at === index ? { ...item, description: event.target.value } : item))} />
                <Input aria-label={`Line ${index + 1} amount`} type="number" step="0.01" value={line.amount} onChange={(event) => setLines(lines.map((item, at) => at === index ? { ...item, amount: event.target.value } : item))} />
                <Button variant="ghost" aria-label={`Remove line ${index + 1}`} onClick={() => setLines(lines.filter((_, at) => at !== index))}>×</Button>
              </div>
            ))}
            <Button variant="outline" onClick={() => setLines([...lines, { description: '', amount: '' }])}>Add line</Button>
            <p className="text-sm text-[var(--bo-muted)]">The order value is the sum of these lines.</p>
          </div>
          <div className="space-y-3">
            <p className="text-sm font-medium">Charge lines</p>
            <p className="text-sm text-[var(--bo-muted)]">Saved with the order. Each one points at this purchase order.</p>
            {charges.map((line, index) => (
              <div key={index} className="space-y-3 rounded border border-[var(--bo-border)] p-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Name"><Input aria-label={`Charge line ${index + 1} name`} value={line.name} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, name: event.target.value } : item))} /></Field>
                  <Field label="Kind">
                    <Select aria-label={`Charge line ${index + 1} kind`} value={line.kind} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, kind: event.target.value } : item))}>
                      {CHARGE_KINDS.map((kind) => <option key={kind} value={kind}>{enumLabel(kind)}</option>)}
                    </Select>
                  </Field>
                  <Field label="Model">
                    <Select aria-label={`Charge line ${index + 1} model`} value={line.chargeModel} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, chargeModel: event.target.value } : item))}>
                      {CHARGE_MODELS.map((model) => <option key={model} value={model}>{enumLabel(model)}</option>)}
                    </Select>
                  </Field>
                  <Field label="Recurrence">
                    <Select aria-label={`Charge line ${index + 1} recurrence`} value={line.recurrence} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, recurrence: event.target.value } : item))}>
                      {CHARGE_RECURRENCE.map((value) => <option key={value} value={value}>{enumLabel(value)}</option>)}
                    </Select>
                  </Field>
                  <Field label="Qty"><Input aria-label={`Charge line ${index + 1} quantity`} type="number" value={line.qty} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, qty: event.target.value } : item))} /></Field>
                  <Field label="Unit price"><Input aria-label={`Charge line ${index + 1} unit price`} type="number" step="0.01" value={line.unit} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, unit: event.target.value } : item))} /></Field>
                  <Field label="Bill to"><Input aria-label={`Charge line ${index + 1} bill to`} value={line.billToName} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, billToName: event.target.value } : item))} /></Field>
                  <Field label="Invoice email"><Input aria-label={`Charge line ${index + 1} invoice email`} value={line.billToEmail} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, billToEmail: event.target.value } : item))} /></Field>
                  <Field label="Bill to address"><TextArea aria-label={`Charge line ${index + 1} address`} value={line.billToAddress} onChange={(event) => setCharges(charges.map((item, at) => at === index ? { ...item, billToAddress: event.target.value } : item))} /></Field>
                </div>
                <Button variant="ghost" onClick={() => setCharges(charges.filter((_, at) => at !== index))}>Remove</Button>
              </div>
            ))}
            <Button variant="outline" onClick={() => setCharges([...charges, { name: '', kind: 'other', chargeModel: 'project', recurrence: 'none', qty: '1', unit: '', billToName: '', billToAddress: '', billToEmail: '' }])}>Add charge line</Button>
          </div>
        </>
      ) : null}
      {action === 'bill' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Vendor"><Select searchable value={vendorId} onChange={(event) => setVendorId(event.target.value)}><option value="">Select</option>{vendors.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</Select></Field>
          <Field label="Bill number"><Input value={number} onChange={(event) => setNumber(event.target.value)} /></Field>
          <Field label="Amount"><Input type="number" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></Field>
          <Field label="Currency"><Select value={currency} onChange={(event) => setCurrency(event.target.value)}><option>GBP</option><option>EUR</option><option>USD</option></Select></Field>
          <Field label="Quantity"><Input type="number" value={qty} onChange={(event) => setQty(event.target.value)} /></Field>
          <Field label="Unit"><Select value={unit} onChange={(event) => setUnit(event.target.value === 'hour' ? 'hour' : 'day')}><option value="day">Day</option><option value="hour">Hour</option></Select></Field>
          <Field label="Period start"><Input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></Field>
          <Field label="Period end"><Input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></Field>
          <Field label="Due"><Input type="date" value={dueOn} onChange={(event) => setDueOn(event.target.value)} /></Field>
        </div>
      ) : null}
      {action === 'expense' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Claimant email"><Input value={claimant} onChange={(event) => setClaimant(event.target.value)} /></Field>
          <Field label="Expense number"><Input value={number} onChange={(event) => setNumber(event.target.value)} /></Field>
          <Field label="Amount"><Input type="number" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} /></Field>
          <Field label="Currency"><Select value={currency} onChange={(event) => setCurrency(event.target.value)}><option>GBP</option><option>EUR</option><option>USD</option></Select></Field>
          <Field label="Due"><Input type="date" value={dueOn} onChange={(event) => setDueOn(event.target.value)} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={preApproved} onChange={(event) => setPreApproved(event.target.checked)} /> Pre-approved</label>
        </div>
      ) : null}
      {action === 'offer' ? (
        <div className="grid gap-4">
          <div className="flex gap-2">
            {(['proposal', 'quotation', 'both'] as const).map((choice) => (
              <button key={choice} type="button" className={`rounded-md border px-3 py-1.5 text-sm ${cover === choice ? 'border-[#895CF6] text-[var(--bo-text)]' : 'border-[var(--bo-border)] text-[var(--bo-muted)]'}`} onClick={() => { setCover(choice); setOfferTab(choice === 'quotation' ? 'quotation' : 'proposal'); }}>{choice === 'both' ? 'Both' : choice === 'quotation' ? 'Quotation only' : 'Proposal only'}</button>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Customer"><Select searchable choices={customerChoices(customers)} onOpen={reloadCustomers} value={customerId} onChange={(event) => setCustomerId(event.target.value)} /></Field>
            <Field label="Engagement"><Select searchable choices={engagementChoices(engagements)} onOpen={reloadEngagements} value={engagementId} onChange={(event) => setEngagementId(event.target.value)} /></Field>
            <Field label="Title"><Input value={offerTitle} onChange={(event) => setOfferTitle(event.target.value)} /></Field>
            <Field label="Kind">
              <Select value={docKind} onChange={(event) => setDocKind(event.target.value)}>
                <option value="services">Services</option>
                <option value="licence">Licence</option>
                <option value="support">Support</option>
                <option value="commercial">Commercial</option>
              </Select>
            </Field>
            <Field label="Currency"><Select value={currency} onChange={(event) => setCurrency(event.target.value)}><option>GBP</option><option>EUR</option><option>USD</option></Select></Field>
            <Field label="Bill to"><Input value={billToName} onChange={(event) => setBillToName(event.target.value)} /></Field>
            <Field label="Address"><TextArea value={billToAddress} onChange={(event) => setBillToAddress(event.target.value)} /></Field>
            <Field label="Email"><Input value={billToEmail} onChange={(event) => setBillToEmail(event.target.value)} /></Field>
          </div>
          {cover === 'both' ? (
            <div className="flex gap-1 border-b border-[var(--bo-border)]">
              {(['proposal', 'quotation'] as const).map((tab) => (
                <button key={tab} type="button" className={`border-b-2 px-3 py-2 text-sm ${offerTab === tab ? 'border-[#895CF6] text-[var(--bo-text)]' : 'border-transparent text-[var(--bo-muted)]'}`} onClick={() => setOfferTab(tab)}>{tab === 'proposal' ? 'Proposal' : 'Quotation'}</button>
              ))}
            </div>
          ) : null}
          {cover !== 'quotation' && (cover === 'proposal' || offerTab === 'proposal') ? (
            <div className="grid gap-4">
              <Field label="Prepared for"><Input value={preparedFor} onChange={(event) => setPreparedFor(event.target.value)} /></Field>
              <Field label="Purpose"><TextArea value={purpose} onChange={(event) => setPurpose(event.target.value)} /></Field>
              <Field label="Scope"><TextArea value={scope} onChange={(event) => setScope(event.target.value)} /></Field>
              <Field label="Summary"><TextArea value={summary} onChange={(event) => setSummary(event.target.value)} /></Field>
            </div>
          ) : null}
          {cover !== 'proposal' && (cover === 'quotation' || offerTab === 'quotation') ? (
            <div className="space-y-2">
              <div className="grid grid-cols-[minmax(0,1fr)_80px_140px_40px] gap-2 text-xs text-[var(--bo-muted)]"><span>Description</span><span>Qty</span><span>Unit price</span><span /></div>
              {quoteLines.map((line, index) => (
                <div key={index} className="grid grid-cols-[minmax(0,1fr)_80px_140px_40px] gap-2">
                  <Input aria-label={`Quote line ${index + 1} description`} value={line.description} onChange={(event) => setQuoteLines(quoteLines.map((item, at) => at === index ? { ...item, description: event.target.value } : item))} />
                  <Input aria-label={`Quote line ${index + 1} quantity`} type="number" value={line.qty} onChange={(event) => setQuoteLines(quoteLines.map((item, at) => at === index ? { ...item, qty: event.target.value } : item))} />
                  <Input aria-label={`Quote line ${index + 1} unit price`} type="number" step="0.01" value={line.unit} onChange={(event) => setQuoteLines(quoteLines.map((item, at) => at === index ? { ...item, unit: event.target.value } : item))} />
                  <Button variant="ghost" aria-label={`Remove quote line ${index + 1}`} onClick={() => setQuoteLines(quoteLines.filter((_, at) => at !== index))}>×</Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => setQuoteLines([...quoteLines, { description: '', qty: '1', unit: '' }])}>Add line</Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {action === 'accept' ? (
        <Field label="Issued offer">
          <Select searchable value={offerId} onChange={(event) => setOfferId(event.target.value)}>
            <option value="">Select</option>
            {offers.map((row) => <option key={`${row.kind}-${row.id}`} value={`${row.kind}:${row.id}`}>{row.number} {row.title}</option>)}
          </Select>
        </Field>
      ) : null}
      <div className="space-y-3 border-t border-[var(--bo-border)] pt-4">
        <p className="text-sm font-medium">Assistant</p>
        <p className="text-sm text-[var(--bo-muted)]">Send an instruction to amend this draft. Nothing is saved until you create the record.</p>
        <div className="max-h-40 space-y-2 overflow-y-auto">
          {chat.map((turn, index) => (
            <p key={index} className={turn.role === 'user' ? 'text-sm' : 'text-sm text-[var(--bo-muted)]'}>{turn.role === 'user' ? 'You' : 'Assistant'}: {turn.text}</p>
          ))}
        </div>
        <TextArea value={followUp} onChange={(event) => setFollowUp(event.target.value)} placeholder="For example: make the cloud line hosting, monthly." />
        <Button variant="outline" disabled={revising || !followUp.trim()} onClick={() => void sendFollowUp()}>Send</Button>
      </div>
    </Dialog>
    {askCreate ? (
      <Dialog title="Create engagement" onClose={() => { if (!drafting) setAskCreate(false); }} footer={
        <>
          <Button variant="outline" disabled={drafting} onClick={() => setAskCreate(false)}>Cancel</Button>
          <Button disabled={drafting} onClick={() => void proceedCreate()}>Proceed</Button>
        </>
      }>
        <p className="text-sm">Create an engagement for this order? The draft uses the email, the attachment, and anything you add here.</p>
        <Field label="Further instruction">
          <TextArea value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder="Optional. Leave blank to draft from the order alone." />
        </Field>
      </Dialog>
    ) : null}
    {engagementDraft ? (
      <Dialog title="Engagement draft" onClose={() => setEngagementDraft(null)} footer={
        <>
          <Button variant="outline" onClick={() => setEngagementDraft(null)}>Cancel</Button>
          <Button onClick={() => void saveEngagement()}>Save and select</Button>
        </>
      }>
        <p className="text-sm text-[var(--bo-muted)]">Change anything that is wrong. Save and select stores the engagement and chooses it on this order. Cancel leaves the order draft as it is.</p>
        <Field label="Customer">
          <Select searchable choices={customerChoices(customers, 'New customer')} onOpen={reloadCustomers} value={engagementDraft.customerId} onChange={(event) => setEngagementDraft({ ...engagementDraft, customerId: event.target.value })} />
        </Field>
        {engagementDraft.customerId ? null : (
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="New customer name"><Input value={engagementDraft.customerName} onChange={(event) => setEngagementDraft({ ...engagementDraft, customerName: event.target.value })} /></Field>
            <Field label="Code"><Input value={engagementDraft.customerCode} onChange={(event) => setEngagementDraft({ ...engagementDraft, customerCode: event.target.value })} /></Field>
          </div>
        )}
        <Field label="Name"><Input value={engagementDraft.name} onChange={(event) => setEngagementDraft({ ...engagementDraft, name: event.target.value })} /></Field>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Status">
            <Select value={engagementDraft.status} onChange={(event) => setEngagementDraft({ ...engagementDraft, status: event.target.value })}>
              <option value="proposed">Proposed</option>
              <option value="contracted">Contracted</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </Select>
          </Field>
          <Field label="Stage">
            <Select value={engagementDraft.stage} onChange={(event) => setEngagementDraft({ ...engagementDraft, stage: event.target.value })}>
              <option value="lead">Lead</option>
              <option value="opportunity">Opportunity</option>
              <option value="proposal">Proposal</option>
              <option value="quotation">Quotation</option>
              <option value="contracted">Contracted</option>
              <option value="closed">Closed</option>
            </Select>
          </Field>
        </div>
      </Dialog>
    ) : null}
    </>
  );
}
